import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { Layout, TableSkeleton, PageHeader, StatCard, SectionCard, FilterBar, FilterField, EmptyState, StatusTag } from '../common';
import { Select, DatePicker, Table, Button, Skeleton } from 'antd';
import {
    Activity,
    FilterX,
    Users,
    Store,
    ClipboardCheck,
    CalendarDays,
    DollarSign,
    ShieldAlert,
    TriangleAlert,
    RotateCw,
    MapPin,
    UserRound,
    Building2,
    Inbox
} from 'lucide-react';
import UsoDeShelfController, { cap, ROLE_OPTIONS, DEFAULT_FILTERS } from './usoDeShelf.controller';
import { UsageCalendar, monthHeatColumns, monthLabel } from './usageHeat';
import PricePill from './elements/PricePill';
import UsageLegend from './elements/UsageLegend';
import dayjs from 'dayjs';
import { brand } from '../../theme/tokens';

const { RangePicker } = DatePicker;

const tablePagination = (noun) => ({
    pageSize: 25,
    showSizeChanger: true,
    pageSizeOptions: ['10', '25', '50', '100'],
    showTotal: (t) => `${t} ${noun}`
});

const emptyLocale = { emptyText: <EmptyState icon={<Inbox />} title="Sin datos" description="No hay actividad para los filtros seleccionados." /> };

/** "Meses: Ene 2026 · Feb 2026" footer under heat tables. */
const MonthsFooter = ({ months }) =>
    months.length ? (
        <div className="border-t border-surface-100 px-5 py-3 text-xs text-surface-400">Meses: {months.map(monthLabel).join(' · ')}</div>
    ) : null;

/** Colored tag dot for the Etiqueta select options. */
const tagOption = (opt) => (
    <span className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: opt.data.color || brand[500] }} />
        {opt.data.label}
    </span>
);

class UsoDeShelf extends Inherit(UsoDeShelfController, Component) {
    state = {
        loading: true,
        dataLoading: false,
        accessDenied: false,
        error: false,
        users: [],
        tagOptions: [],
        clientOptions: [],
        data: null,
        filters: { ...DEFAULT_FILTERS },
        dateRange: [dayjs().subtract(90, 'day'), dayjs()]
    };

    // "Usuario" column (name + role tag), reused by the per-user and per-client-drill tables.
    userNameColumn(width = 220) {
        return {
            title: 'Usuario',
            dataIndex: 'userName',
            key: 'userName',
            fixed: 'left',
            width,
            render: (name, r) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-surface-900">{name}</span>
                    {r.userType && r.userType !== '—' ? <StatusTag className="!h-5 !px-1.5">{cap(r.userType)}</StatusTag> : null}
                    {this.hidePrices ? null : <PricePill count={r.priceCount} />}
                </div>
            )
        };
    }

    get userColumns() {
        const { data } = this.state;
        const months = data?.months || [];
        return [this.userNameColumn(), ...monthHeatColumns(data?.byUser || [], months)];
    }

    get clientColumns() {
        const { data } = this.state;
        const months = data?.months || [];
        return [
            {
                title: 'Cliente',
                dataIndex: 'clientName',
                key: 'clientName',
                fixed: 'left',
                width: 300,
                render: (t, r) => (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-surface-900">{t}</span>
                        {this.hidePrices ? null : <PricePill count={r.priceCount} />}
                    </div>
                )
            },
            ...monthHeatColumns(data?.byClient || [], months)
        ];
    }

    get storeColumns() {
        const { data } = this.state;
        const months = data?.months || [];
        return [
            {
                title: 'PDV (Piso de venta)',
                dataIndex: 'storeName',
                key: 'storeName',
                fixed: 'left',
                width: 320,
                render: (t, r) => (
                    <div className="flex flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-surface-900">{t}</span>
                            {this.hidePrices ? null : <PricePill count={r.priceCount} />}
                        </div>
                        {r.clientName ? <span className="text-xs text-surface-400">{r.clientName}</span> : null}
                    </div>
                )
            },
            ...monthHeatColumns(data?.byStore || [], months)
        ];
    }

    // Expanded client/store row → the users who visited it, on their own heat scale.
    renderClientUsers(record) {
        const months = this.state.data?.months || [];
        const rows = record.users || [];
        const parentKey = record.storeId || record.clientId || record.clientName;
        return (
            <Table
                className="heat-table"
                columns={[this.userNameColumn(200), ...monthHeatColumns(rows, months)]}
                dataSource={rows}
                rowKey={(r) => `${parentKey}-${r.userId || r.userName}`}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
        );
    }

    renderStats() {
        const { totals, avgPerDay, drillStores, coordinateMode } = this.usageSummary();
        // In coordinate mode the metric is coordinate updates, not store-check visits.
        const activityLabel = coordinateMode ? 'Actualizaciones' : 'Visitas totales';
        const cols = this.hidePrices ? 'xl:grid-cols-4' : 'xl:grid-cols-5';

        return (
            <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${cols} mb-4`}>
                <StatCard label={activityLabel} value={totals.visits.toLocaleString()} icon={<ClipboardCheck />} tone="brand" />
                {this.hidePrices ? null : (
                    <StatCard
                        label="Store-checks con precios"
                        value={(totals.prices || 0).toLocaleString()}
                        icon={<DollarSign />}
                        tone="success"
                        help="Store-checks con al menos un precio capturado en el rango."
                    />
                )}
                <StatCard
                    label={coordinateMode ? 'Usuarios' : 'Usuarios activos'}
                    value={totals.users.toLocaleString()}
                    icon={<Users />}
                    tone="info"
                />
                <StatCard
                    label={drillStores ? 'PDV auditados' : coordinateMode ? 'Clientes actualizados' : 'Clientes visitados'}
                    value={(drillStores ? totals.stores || 0 : totals.clients).toLocaleString()}
                    icon={<Store />}
                    tone="violet"
                />
                <StatCard
                    label="Promedio por día activo"
                    value={avgPerDay.toLocaleString()}
                    icon={<CalendarDays />}
                    tone="warning"
                    help="Total del período dividido entre los días con al menos una actividad."
                />
            </div>
        );
    }

    renderContent() {
        const { data, dataLoading } = this.state;
        const months = data?.months || [];
        const { drillStores, coordinateMode } = this.usageSummary();
        const noun = coordinateMode ? 'actualizaciones' : 'visitas';

        return (
            <>
                {this.renderStats()}

                <SectionCard
                    className="mb-4"
                    icon={<CalendarDays />}
                    title="Uso general"
                    description={`${coordinateMode ? 'Actualizaciones' : 'Visitas'} por día. Cada fila es un mes.`}
                    extra={<UsageLegend noun={noun} />}>
                    {dataLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : <UsageCalendar daily={data?.daily || []} />}
                </SectionCard>

                <SectionCard
                    flush
                    className="mb-4"
                    icon={<UserRound />}
                    title={coordinateMode ? 'Usuarios que actualizaron coordenadas' : 'Uso por usuario'}
                    description={`Color más oscuro = más ${noun}`}
                    extra={<UsageLegend noun={noun} />}>
                    <Table
                        className="heat-table"
                        columns={this.userColumns}
                        dataSource={data?.byUser || []}
                        loading={dataLoading}
                        rowKey={(r) => r.userId || r.userName}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={tablePagination('usuarios')}
                        locale={emptyLocale}
                    />
                </SectionCard>

                {drillStores ? (
                    <SectionCard
                        flush
                        icon={<Store />}
                        title="PDV auditados por cliente"
                        description="Pisos de venta de los clientes seleccionados · expande para ver usuarios"
                        extra={<UsageLegend noun={noun} />}>
                        <Table
                            className="heat-table"
                            columns={this.storeColumns}
                            dataSource={data?.byStore || []}
                            loading={dataLoading}
                            rowKey={(r) => r.storeId || r.storeName}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            expandable={{ expandedRowRender: (record) => this.renderClientUsers(record), rowExpandable: (r) => (r.users || []).length > 0 }}
                            pagination={tablePagination('PDV')}
                            locale={emptyLocale}
                        />
                        <MonthsFooter months={months} />
                    </SectionCard>
                ) : (
                    <SectionCard
                        flush
                        icon={<Building2 />}
                        title={coordinateMode ? 'Clientes actualizados' : 'Uso por cliente'}
                        description={
                            coordinateMode
                                ? 'Clientes cuyos PDV recibieron actualización de coordenadas · expande para ver usuarios'
                                : 'Selecciona un cliente arriba para ver sus PDV · expande para ver usuarios'
                        }
                        extra={<UsageLegend noun={noun} />}>
                        <Table
                            className="heat-table"
                            columns={this.clientColumns}
                            dataSource={data?.byClient || []}
                            loading={dataLoading}
                            rowKey={(r) => r.clientId || r.clientName}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            expandable={{ expandedRowRender: (record) => this.renderClientUsers(record), rowExpandable: (r) => (r.users || []).length > 0 }}
                            pagination={tablePagination('clientes')}
                            locale={emptyLocale}
                        />
                        <MonthsFooter months={months} />
                    </SectionCard>
                )}
            </>
        );
    }

    renderFilters() {
        const { tagOptions, clientOptions, filters, dateRange } = this.state;

        return (
            <FilterBar
                actions={
                    <Button icon={<FilterX />} onClick={() => this.clearFilters()}>
                        Limpiar
                    </Button>
                }>
                <FilterField label="Rol" width={200}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.userType}
                        options={ROLE_OPTIONS}
                        onChange={(v) => this.setFilter('userType', v || [])}
                    />
                </FilterField>
                <FilterField label="Usuario" width={230}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.userId}
                        options={this.userOptions()}
                        onChange={(v) => this.setFilter('userId', v || [])}
                    />
                </FilterField>
                <FilterField label="Cliente" width={240}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.clientId}
                        options={clientOptions}
                        onChange={(v) => this.setFilter('clientId', v || [])}
                    />
                </FilterField>
                <FilterField label="Etiqueta" width={200}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todas"
                        value={filters.tagId}
                        options={tagOptions}
                        onChange={(v) => this.setFilter('tagId', v || [])}
                        optionRender={tagOption}
                    />
                </FilterField>
                <FilterField label="Rango de fechas" width={250}>
                    <RangePicker value={dateRange} allowClear={false} format="DD/MM/YYYY" onChange={(range) => this.setDateRange(range)} />
                </FilterField>
            </FilterBar>
        );
    }

    render() {
        const { loading, accessDenied, error } = this.state;

        return (
            <Layout current="uso-de-shelf">
                {this.props.contextHolder}

                <PageHeader
                    icon={<Activity />}
                    title="Uso de Shelf"
                    description="Actividad de levantamientos (store checks) por usuario, cliente y fecha. Color más oscuro = más uso."
                    meta={
                        this.coordinateMode ? (
                            <StatusTag tone="info" icon={<MapPin />}>
                                Mostrando actualizaciones de coordenadas
                            </StatusTag>
                        ) : null
                    }
                />

                {accessDenied ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para administradores y gerentes."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="Error al cargar el uso de Shelf"
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.retry()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={10} columns={8} />
                ) : (
                    <div className="animate-in">
                        {this.renderFilters()}
                        {this.renderContent()}
                    </div>
                )}
            </Layout>
        );
    }
}

export default withNavigate(UsoDeShelf);
